// announcerConfig — статическая конфигурация инструмента «Анонсы для соцсетей».
//
// Здесь живут две вещи: реестр режимов анонса (порядок отображения = порядок
// чекбоксов в форме и блоков в модалке результата) и символьные лимиты полей.
//
// Выбор моделей у этого инструмента НЕТ (в отличие от генератора обложек):
// редактор отмечает режимы, а модели фиксированы на бэкенде. Двойная генерация
// (BP-010): каждый отмеченный режим — ДВА платных вызова (Вариант 1 и Вариант
// 2, по одному на модель), поэтому по умолчанию не отмечен ни один режим (см.
// EMPTY_ANNOUNCER_FORM_STATE в useAnnouncerFormState.ts).

/** Слаг режима анонса. Строго совпадает с backend `ANNOUNCER_MODE_SLUGS`
 *  (`apps/prompts/social_announcer.py:57-60`): no_outcome / with_outcome.
 *  При расхождении бэкенд вернёт 400 (ChoiceField по списку слагов).
 *
 *  Ось разделения — ИСХОД материала (развязка, ключевой вывод, ответ на
 *  заданный вопрос): `no_outcome` его прячет, `with_outcome` называет прямо.
 *  Прежняя тройка intriguing / balanced / informative снята вместе с
 *  трёхрежимной схемой; бэкенд старые слаги больше не принимает. */
export type AnnouncerMode = "no_outcome" | "with_outcome";

export interface AnnouncerModeOption {
  /** Слаг — уходит в payload в массиве `modes`. */
  slug: AnnouncerMode;
  /** Подпись чекбокса и заголовок блока результата. */
  label: string;
  /** Подсказка под галочкой режима — отдельная строка под названием.
   *
   *  Имя поля намеренно совпадает с полем `ui_hint` бэкендового реестра
   *  `ANNOUNCER_MODES` (`apps/prompts/social_announcer.py:320`, значения —
   *  строки 333-336 и 347-350): промпт-модуль и есть источник истины для
   *  этих текстов, и одинаковое имя не даёт потерять связь при сверке.
   *  Тексты согласованы с заказчиком ДОСЛОВНО — правка формулировки здесь без
   *  такой же правки на бэкенде разводит интерфейс с первоисточником. */
  uiHint: string;
}

/** Два режима в порядке отображения формы (порядок чекбоксов в
 *  AnnouncerForm). ⚠️ Это НЕ порядок карточек результата: тот определяется
 *  порядком, в котором редактор фактически отмечал чекбоксы (группа чекбоксов
 *  antd в режиме children сортирует по `options`, а он здесь пуст — клики
 *  доезжают как есть). Бэкенд дедуплицирует `modes` (`serializers.py:199-223`)
 *  с сохранением присланного порядка, но не переставляет его под реестр.
 *  Подпись каждой карточки от порядка не зависит — она резолвится по полю
 *  `mode` варианта (см. findAnnouncerMode). */
export const ANNOUNCER_MODES: AnnouncerModeOption[] = [
  {
    slug: "no_outcome",
    label: "Без спойлеров",
    uiHint:
      "Читатель поймёт, о чём материал, но развязку или ключевой вывод узнает только в самом материале.",
  },
  {
    slug: "with_outcome",
    label: "Информативный",
    uiHint:
      "Читатель увидит всю информацию о материале сжато прямо в анонсе, в том числе развязку или ключевой вывод.",
  },
];

/** Найти опцию режима по слагу — для подписи блока результата и в истории.
 *  Возвращает undefined, если пришёл неизвестный код: вызывающий код
 *  обязан дать человекочитаемый фолбэк («Вариант N»), а НЕ показывать сырой
 *  слаг — технические идентификаторы в текстах для пользователя запрещены.
 *
 *  Неизвестный код — не гипотетический случай: в истории генераций лежат записи
 *  со СТАРЫМИ слагами трёхрежимной схемы (intriguing / balanced / informative),
 *  и они никуда не денутся. Поэтому фолбэк у вызывающих обязателен. */
export function findAnnouncerMode(slug: string): AnnouncerModeOption | undefined {
  return ANNOUNCER_MODES.find((m) => m.slug === slug);
}

/** Символьные лимиты полей ввода.
 *
 *  ⚠️ ИСТОЧНИК ИСТИНЫ — БЭКЕНД: константы `ANNOUNCER_HEADLINE_MAX` /
 *  `ANNOUNCER_BODY_MAX` / `ANNOUNCER_WISHES_MAX` в
 *  `backend/apps/prompts/social_announcer.py` (строки 37-39). Числа здесь
 *  продублированы осознанно — чтобы форма могла показать счётчик и обрезать
 *  текст ДО отправки, а не получать 400 постфактум. Сверено 2026-08-25:
 *  500 / 50 000 / 5 000 — совпадают. При изменении лимита на бэкенде правку
 *  сюда нужно перенести руками: автоматической синхронизации нет.
 *
 *  Лимиты символьные (Unicode code points), не байтовые — см. countChars ниже. */
export const ANNOUNCER_LIMITS = {
  headline: 500,
  body: 50_000,
  wishes: 5_000,
} as const;

/** Имена трёх текстовых полей формы — производные от реестра лимитов. */
export type AnnouncerTextField = keyof typeof ANNOUNCER_LIMITS;

// Подсчёт и обрезка длины — ЕДИНЫЙ на весь проект источник истины: утилиты
// генератора обложек. Они считают Unicode code points, как `len()` в Python на
// бэкенде; обычный `str.length` считает UTF-16 code units и на эмодзи и
// суррогатных парах разойдётся с серверным лимитом (эмодзи = 2 units, но 1 code
// point). Третьей реализации подсчёта в проекте быть не должно, поэтому здесь
// только ре-экспорт: страница инструмента может брать truncateChars отсюда,
// не зная про папку обложек.
export {
  countChars,
  truncateChars,
} from "@/pages/tools/PublicationCover/coverConfig";

// ---------- Двойная генерация: клиентские пороги и таймауты (BP-010 §8.2) ----------
//
// Цифры ниже — стартовые пороги: решение оркестратора 2026-09-17, на
// утверждение владельцу (§13 В6 спецификации), не замер: сверяются на этапе 5
// по факту работы обеих моделей на боевых поставщиках. Порог «дольше
// обычного» 60 с у ANNOUNCER_WAIT_SLOW_AFTER_S — тоже трактовка оркестратора,
// на утверждение (см. докстринг константы): решение владельца № 8 — это
// критерий ПРИЁМКИ по скорости работы инструмента («обычно до 30 секунд,
// выбросы до 60 секунд», p75 ≤ 30 с), а не порог интерфейса; то, что этот
// порог интерфейса численно СОВПАДАЕТ с «выбросами» из критерия — выбор
// оркестратора, а не отдельное решение владельца про UI.

/** Таймаут отправки запуска (свой; глобальные 30 с — api/constants.ts — не трогаем). */
export const ANNOUNCER_SUBMIT_TIMEOUT_MS = 60_000;
/** «Дольше обычного» — порог интерфейса, выведенный оркестратором из критерия
 *  владельца № 8 «выбросы до 60 секунд» (PRD, `01_prd_dual_generation.md`) —
 *  тоже на утверждение владельцу, не готовое решение по UI. */
export const ANNOUNCER_WAIT_SLOW_AFTER_S = 60;
/** Клиентский предел ожидания варианта: опрос останавливается, «Проверить ещё раз» (бесплатно). */
export const ANNOUNCER_WAIT_LIMIT_S = 300;
/** Зеркало backend ANNOUNCER_STALE_AFTER_S (backend/apps/generations/tasks.py) — менять ВМЕСТЕ;
 *  бэкенд-значение закреплено тестом test_social_announcer_task.py::test_soft_time_limit_below_celery_soft.
 *  База отсчёта разная: elapsed_s опроса — от создания записи, а сервер для processing считает от якоря
 *  бюджета (views._announcer_variant_age_s). Поэтому первые queued_s секунд после порога повтор получает
 *  409 «Вариант ещё генерируется…» — это ожидаемо, текст показывается под кнопкой. Трактовка «до
 *  признания варианта зависшим» и само число 760 — решение оркестратора 2026-09-17, на утверждение
 *  владельцу (см. TextVariantResultBlock.tsx). */
export const ANNOUNCER_STALE_RETRY_AFTER_S = 760;
/** Пауза перед единственным повтором отметки «копировался» при обрыве связи/5xx. */
export const ANNOUNCER_COPY_MARK_RETRY_DELAY_MS = 3_000;
